import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { fileDetailsStore, TaskRelationInfo, WorkflowRelationInfo } from "../FileDetailsStore";
import { Spin } from "Components/General/Spin";
import { Badge, Row, Col } from "antd";
import { RelationsTableRowActions } from "./RelationsTableRowActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { dayjs } from "Config/dayjs";
import { TaskStateTag } from "Pages/Project/Tasks/Components/TaskStateTag";
import { Table } from "Components/General/Table";
import { ColumnsType } from "antd/es/table";

interface RelationsProps extends WithTranslation {}
interface RelationsState {
  loading: boolean;
}
export const Relations = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<RelationsProps, RelationsState> {
      constructor(props: RelationsProps) {
        super(props);
        this.state = {
          loading: false,
        };
      }
      componentDidMount() {
        this.handleFetch();
      }

      async handleFetch() {
        try {
          this.setState({ loading: true });
          await fileDetailsStore.fetchRelations();
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      }

      get columns(): ColumnsType<API_RelationInfo & { idx?: number }> {
        return [
          {
            dataIndex: "path",
          },
          {
            dataIndex: "actions",
            render: (_text, record: API_RelationInfo, _index) => {
              const { fileId, dirId, deleted, manageItem, type } = record;
              return (
                !deleted && (
                  <RelationsTableRowActions data={{ fileId, dirId, manageItem, type }}>
                    <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
                      <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
                    </div>
                  </RelationsTableRowActions>
                )
              );
            },
          },
        ];
      }

      get columnsTask(): ColumnsType<TaskRelationInfo> {
        return [
          {
            dataIndex: "name",
          },
          {
            dataIndex: "dueDate",
            render: (text) => (text ? dayjs(+text).format("lll") : "-"),
          },
          {
            dataIndex: "state",
            render: (_text, record: TaskRelationInfo) => <TaskStateTag state={record.state} />,
          },
          {
            dataIndex: "actions",
            render: (_text, record: TaskRelationInfo, _index) => {
              const { deleted } = record;
              return (
                !deleted && (
                  <RelationsTableRowActions data={{ task: record }}>
                    <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
                      <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
                    </div>
                  </RelationsTableRowActions>
                )
              );
            },
          },
        ];
      }

      get columnsWorkflow(): ColumnsType<WorkflowRelationInfo> {
        return [
          {
            dataIndex: "workflowName",
          },
          {
            dataIndex: "keyBezeichnung",
          },
          {
            dataIndex: "lastaction",
            render: (text) => (text ? dayjs(+text).format("lll") : "-"),
          },
          {
            dataIndex: "actions",
            render: (_text, record: WorkflowRelationInfo, _index) => {
              const { accessToDetails, id } = record;
              return (
                accessToDetails && (
                  <RelationsTableRowActions data={{ workflow: id }}>
                    <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
                      <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
                    </div>
                  </RelationsTableRowActions>
                )
              );
            },
          },
        ];
      }

      getRowClassName = ({ deleted }: API_RelationInfo) => classnames({ disabledCell: deleted });

      render() {
        const { t } = this.props;
        const { loading } = this.state;
        const { fileRelations } = fileDetailsStore;
        const count = fileRelations.MASTER.filter((item) => !item.deleted).length;
        return (
          <Spin spinning={loading}>
            <div>
              {fileRelations.ORIGINAL.length ? (
                <>
                  <Row justify="space-between">
                    <Col>
                      <h3>{t("Is linked from")}:</h3>
                    </Col>
                    <Col>
                      <Badge count={count} />
                    </Col>
                  </Row>
                  <Table<API_RelationInfo & { idx?: number }>
                    name="relationLinkedFrom"
                    showHeader={false}
                    pagination={false}
                    dataSource={fileRelations.ORIGINAL as Array<API_RelationInfo & { idx?: number }>}
                    columns={this.columns}
                    rowKey={(record) => `${record.idx}`}
                    className="mb-md"
                    rowClassName={this.getRowClassName}
                  />
                </>
              ) : null}
              {fileRelations.MASTER.length ? (
                <>
                  <Row justify="space-between">
                    <Col>
                      <h3>{t("Is master of")}:</h3>
                    </Col>
                    <Col>
                      <Badge count={count} />
                    </Col>
                  </Row>
                  <Table<API_RelationInfo & { idx?: number }>
                    name="relationMasterOf"
                    showHeader={false}
                    pagination={false}
                    dataSource={fileRelations.MASTER as Array<API_RelationInfo & { idx?: number }>}
                    columns={this.columns}
                    rowKey={(record) => `${record.idx}`}
                    className="mb-md"
                    rowClassName={this.getRowClassName}
                  />
                </>
              ) : null}
              {fileRelations.SLAVE.length ? (
                <>
                  <h3>{t("Is slave of")}:</h3>
                  <Table<API_RelationInfo & { idx?: number }>
                    name="relationSlaveOF"
                    showHeader={false}
                    pagination={false}
                    dataSource={fileRelations.SLAVE as Array<API_RelationInfo & { idx?: number }>}
                    columns={this.columns}
                    rowKey={(record) => `${record.idx}`}
                    className="mb-md"
                    rowClassName={this.getRowClassName}
                  />
                </>
              ) : null}
              {fileRelations.SYMLINK.length ? (
                <>
                  <h3>{t("Is link to")}:</h3>
                  <Table<API_RelationInfo & { idx?: number }>
                    name="relationLinkTo"
                    showHeader={false}
                    pagination={false}
                    dataSource={fileRelations.SYMLINK as Array<API_RelationInfo & { idx?: number }>}
                    columns={this.columns}
                    rowKey={(record) => `${record.idx}`}
                    className="mb-md"
                    rowClassName={this.getRowClassName}
                  />
                </>
              ) : null}
              {fileRelations.WORKFLOW.length ? (
                <>
                  <h3>{t("Linked workflows")}:</h3>
                  <Table<API_RelationInfo & { idx?: number }>
                    name="relationWorkflows"
                    showHeader={false}
                    pagination={false}
                    dataSource={fileRelations.WORKFLOW as Array<API_RelationInfo & { idx?: number }>}
                    columns={this.columnsWorkflow}
                    rowKey={(record) => `${record.idx}`}
                    className="mb-md"
                    rowClassName={this.getRowClassName}
                  />
                </>
              ) : null}
              {fileRelations.TASK.length ? (
                <>
                  <h3>{t("Relative to tasks")}:</h3>
                  <Table<API_RelationInfo & { idx?: number }>
                    name="relativeTasks"
                    showHeader={false}
                    pagination={false}
                    dataSource={fileRelations.TASK as Array<API_RelationInfo & { idx?: number }>}
                    columns={this.columnsTask}
                    rowKey={(record) => `${record.idx}`}
                    className="mb-md"
                    rowClassName={this.getRowClassName}
                  />
                </>
              ) : null}
            </div>
          </Spin>
        );
      }
    }
  )
);
